import { createSelector } from "reselect";

import salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";
import { ExcludeCategoryFilterKey } from "app/pages/sales-intelligence/sub-modules/advanced-search/types/filters-keys";
import { selectLegacyWorkspacesFetching } from "app/pages/sales-intelligence/sub-modules/common/store/selectors";
import {
    selectHubspotLoadingState,
    selectIsHubspotLeadsFilterEnabled,
} from "app/pages/sales-intelligence/sub-modules/management/store/selectors/hubspotSelectors";
import {
    selectIsSalesforceLeadsFilterEnabled,
    selectSalesforceLoadingState,
} from "app/pages/sales-intelligence/sub-modules/management/store/selectors/salesforceSelectors";
import { createStatePropertySelector } from "app/pages/workspace/sales/helpers";
import type { RootState } from "single-spa/store/types";

import { selectSalesIntelligenceState } from "../../../store/selectors";
import { keepOnlyUniqueElements } from "../../../utils/arrays";
import { selectAvailableCrmLists, selectIsFetchingCrmLists } from "../../management/store/selectors/crmListSelectors";
import { getCrmListName, makeCrmListId } from "../helpers/crmList";
import {
    opportunityListHasId,
    reduceToListOfOpportunities,
    sortOpportunityListsByFriendlyName,
} from "../helpers/helpers";
import type {
    CrmOpportunityListOption,
    ExcludeListsAvailability,
    ExcludeListsDuplicateOpportunities,
    OpportunityListOption,
    OpportunityListType,
    StaticOpportunityListOption,
} from "../types";
import { OpportunityListTypeEnum, OpportunityListVariant } from "../types";

import { buildExcludeListsFromExcludeFilters } from "./utils/selectors-utils";

const select = createStatePropertySelector(selectSalesIntelligenceState("opportunities"));

export const selectOpportunityLists = select("opportunityLists");
export const selectOpportunityListCreating = select("opportunityListCreating");
export const selectOpportunityListUpdating = select("opportunityListUpdating");
export const selectOpportunityListDeleting = select("opportunityListDeleting");
export const selectOpportunityListModal = select("opportunityListModal");
export const selectOpportunityListRenameModalIsOpen = select("isListRenameModalOpen");
export const selectOpportunityListDeleteModalIsOpen = select("isListDeleteModalOpen");
export const selectListRecommendations = select("recommendations");
export const selectListRecommendationsFetching = select("recommendationsFetching");
export const selectListRecommendationsOpen = select("recommendationsBarOpen");
export const selectOpportunityListId = select("opportunityListId");
export const selectOpportunityListIdToShowUpdatedListModal = select("opportunityListIdToShowUpdatedListModal");
export const selectIsExcludeDuplicateOpportunitiesModalOpen = select("isExcludeListsDuplicateOpportunitiesModalOpen");
export const selectExcludeListsDuplicateOpportunitiesFetching = select("excludeListsDuplicateOpportunitiesFetching");
const selectExcludeListsDuplicateOpportunities = select("excludeListsDuplicateOpportunities");
export const selectShouldSetFiltersFromSavedSearch = select("shouldWaitToFiltersFromSavedSearch");

const selectExcludeListsAvailability = createSelector(
    [selectIsSalesforceLeadsFilterEnabled, selectIsHubspotLeadsFilterEnabled],
    (isSalesforceIntegrationEnabled, isHubspotIntegrationEnabled): ExcludeListsAvailability => ({
        [ExcludeCategoryFilterKey.Salesforce]: isSalesforceIntegrationEnabled,
        [ExcludeCategoryFilterKey.Hubspot]: isHubspotIntegrationEnabled,
    }),
);

export const selectExcludeLists = createSelector(
    [selectExcludeListsDuplicateOpportunities, selectExcludeListsAvailability],
    (
        excludeListsDuplicateOpportunities,
        excludeIntegrationFiltersAvailability,
    ): ExcludeListsDuplicateOpportunities => ({
        distinctDuplicatesCount: excludeListsDuplicateOpportunities?.distinctDuplicatesCount ?? 0,
        lists: buildExcludeListsFromExcludeFilters(
            excludeListsDuplicateOpportunities,
            excludeIntegrationFiltersAvailability,
        ),
    }),
);

export const selectHasExcludeDuplicatesLists = createSelector(
    selectExcludeLists,
    ({ distinctDuplicatesCount, lists }): boolean => distinctDuplicatesCount >= 1 && lists.length > 0,
);

export const selectHasOpportunityLists = createSelector(selectOpportunityLists, (lists) => lists.length > 0);

export const selectSortedOpportunityLists = createSelector(selectOpportunityLists, sortOpportunityListsByFriendlyName);

const selectStaticOpportunityListOptions = createSelector(
    selectSortedOpportunityLists,
    (opportunityLists): StaticOpportunityListOption[] => {
        return opportunityLists.map((list) => ({
            id: list.opportunityListId,
            variant: OpportunityListVariant.Static,
            name: list.friendlyName,
            opportunitiesCount: list.opportunities.length,
        }));
    },
);

const selectCrmOpportunityListOptions = createSelector(
    selectAvailableCrmLists,
    (crmLists): CrmOpportunityListOption[] => {
        if (!salesSettingsHelper.isCrmListEnabled()) {
            return [];
        }

        return crmLists.map((list) => ({
            id: makeCrmListId(list.integrationName, list.objectType),
            variant: OpportunityListVariant.Crm,
            name: getCrmListName(list.integrationName, list.objectType),
            opportunitiesCount: list.syncedDomainsCount,
        }));
    },
);

const selectOpportunityListOptions = createSelector(
    selectCrmOpportunityListOptions,
    selectStaticOpportunityListOptions,
    (crmLists: OpportunityListOption[], staticLists) => crmLists.concat(staticLists),
);

export const selectGroupedOpportunityListOptions = createSelector(
    selectCrmOpportunityListOptions,
    selectStaticOpportunityListOptions,
    (crmLists, staticLists) => ({
        [OpportunityListVariant.Crm]: crmLists,
        [OpportunityListVariant.Static]: staticLists,
    }),
);

export const selectOpportunityListOptionById = createSelector(
    [selectOpportunityListOptions, (_: RootState, id: string) => id],
    (opportunityLists, id) => opportunityLists.find((list) => list.id === id),
);

export const selectRecentlyViewedOpportunityList = createSelector(selectOpportunityLists, (lists) =>
    lists.find((l) => l.listType === OpportunityListTypeEnum.RecentlyViewed),
);

export const selectIsRecentlyViewedOpportunityList = createSelector(
    selectRecentlyViewedOpportunityList,
    (_: RootState, listId?: string) => listId,
    (recentlyViewedOpportunityList, listId) => listId === recentlyViewedOpportunityList?.opportunityListId,
);

const selectAllQuotaIncludedOpportunityLists = createSelector(selectOpportunityLists, (lists: OpportunityListType[]) =>
    lists.filter((l) => l.listType !== OpportunityListTypeEnum.RecentlyViewed),
);

const selectAllQuotaIncludedWebsites = createSelector(
    selectAllQuotaIncludedOpportunityLists,
    reduceToListOfOpportunities,
);

export const selectAllUniqueWebsites = createSelector(selectAllQuotaIncludedWebsites, keepOnlyUniqueElements);

export const selectListById = createSelector(
    [selectOpportunityLists, (_: RootState, id: string) => id],
    (lists, id) => {
        return lists.find(opportunityListHasId(id));
    },
);

export const selectHasOpportunityListById = createSelector(
    [selectOpportunityLists, selectAvailableCrmLists, (_: RootState, id: string) => id],
    (staticLists, crmLists, id) => {
        const foundInStaticLists = staticLists.some(opportunityListHasId(id));
        const foundInCrmLists = crmLists.some((list) => makeCrmListId(list.integrationName, list.objectType) === id);

        return foundInStaticLists || foundInCrmLists;
    },
);

export const selectIsLoadingListsPage = createSelector(
    selectSalesforceLoadingState,
    selectHubspotLoadingState,
    selectIsFetchingCrmLists,
    selectLegacyWorkspacesFetching,
    selectOpportunityLists,
    (isLoadingSfState, isLoadingHsState, isLoadingCrmLists, isLoadingOpportunitiesList, opportunitiesLists) => {
        return (
            (isLoadingOpportunitiesList && opportunitiesLists.length === 0) ||
            isLoadingSfState ||
            isLoadingHsState ||
            isLoadingCrmLists
        );
    },
);

export const selectIsFetchingOpportunityLists = createSelector(
    selectLegacyWorkspacesFetching,
    selectIsFetchingCrmLists,
    (isFetchingStaticLists, isFetchingCrmLists) => {
        return isFetchingStaticLists || isFetchingCrmLists;
    },
);
